import Phaser from 'phaser'
import { GameScene } from './GameScene'
import { DeathScreen } from '../ui/DeathScreen'
import { ActionButtons } from '../ui/ActionButtons'
import { InventoryUI } from '../ui/InventoryUI'
import { EVENTS, LevelUpEvent, INVENTORY_EVENTS } from '../types/GameTypes'

export class UIScene extends Phaser.Scene {
  private gameScene!: GameScene
  private healthContainer!: Phaser.GameObjects.Container
  private minimapContainer!: Phaser.GameObjects.Container
  private minimapPlayer!: Phaser.GameObjects.Graphics
  private positionText!: Phaser.GameObjects.Text
  private controlHint?: Phaser.GameObjects.Text

  // Dynamic bars
  private healthBarFill!: Phaser.GameObjects.Graphics
  private manaBarFill!: Phaser.GameObjects.Graphics
  private xpBarFill!: Phaser.GameObjects.Graphics
  private healthText!: Phaser.GameObjects.Text
  private manaText!: Phaser.GameObjects.Text
  private levelText!: Phaser.GameObjects.Text
  private xpText!: Phaser.GameObjects.Text

  // Target info
  private targetContainer!: Phaser.GameObjects.Container
  private targetNameText!: Phaser.GameObjects.Text
  private targetHealthBarFill!: Phaser.GameObjects.Graphics
  private targetHealthText!: Phaser.GameObjects.Text

  // Death screen
  private deathScreen!: DeathScreen

  // Action buttons
  private actionButtons!: ActionButtons

  // Inventory UI
  private inventoryUI!: InventoryUI
  private inventoryButton!: Phaser.GameObjects.Container
  private goldDisplay!: Phaser.GameObjects.Container
  private goldAmountText!: Phaser.GameObjects.Text

  // Level up notification
  private levelUpContainer?: Phaser.GameObjects.Container

  constructor() {
    super({ key: 'UIScene' })
  }

  create(): void {
    // Get reference to game scene
    this.gameScene = this.scene.get('GameScene') as GameScene

    // Create UI elements
    this.createHealthBar()
    this.createMinimap()
    this.createPositionIndicator()
    this.createControlHints()
    this.createTargetInfo()
    this.createDeathScreen()
    this.createActionButtons()
    this.createGoldDisplay()
    this.createInventoryButton()
    this.createInventoryUI()

    // Listen for events
    this.setupEventListeners()

    // Handle resize
    this.scale.on('resize', this.handleResize, this)
    this.handleResize()
  }

  private setupEventListeners(): void {
    // Listen for stats changes
    this.gameScene.events.on(EVENTS.STATS_CHANGED, this.updateStatBars, this)

    // Listen for target changes
    this.gameScene.events.on(EVENTS.TARGET_CHANGED, this.updateTargetInfo, this)

    // Listen for level ups
    this.gameScene.events.on(EVENTS.LEVEL_UP, this.showLevelUpNotification, this)

    // Listen for gold changes
    this.gameScene.events.on(INVENTORY_EVENTS.GOLD_CHANGED, this.updateGoldDisplay, this)
  }

  private createHealthBar(): void {
    const padding = 15

    // Container for health display
    this.healthContainer = this.add.container(padding, padding)

    // Background
    const healthBg = this.add.graphics()
    healthBg.fillStyle(0x1f2937, 0.9)
    healthBg.fillRoundedRect(0, 0, 180, 85, 10)
    healthBg.lineStyle(2, 0x4b5563)
    healthBg.strokeRoundedRect(0, 0, 180, 85, 10)

    // Level display
    this.levelText = this.add.text(10, 8, 'Level 1', {
      fontSize: '14px',
      color: '#fbbf24',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    })

    // Health bar background
    const hpBarBg = this.add.graphics()
    hpBarBg.fillStyle(0x333333, 1)
    hpBarBg.fillRoundedRect(10, 28, 120, 14, 4)

    // Health bar fill
    this.healthBarFill = this.add.graphics()
    this.drawHealthBar(1)

    // Health icon (heart)
    const healthIcon = this.add.graphics()
    healthIcon.fillStyle(0xef4444)
    healthIcon.fillCircle(145, 35, 8)

    // Health text
    this.healthText = this.add.text(60, 30, '100/100', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Arial'
    }).setOrigin(0.5, 0)

    // Mana bar background
    const mpBarBg = this.add.graphics()
    mpBarBg.fillStyle(0x333333, 1)
    mpBarBg.fillRoundedRect(10, 46, 120, 14, 4)

    // Mana bar fill
    this.manaBarFill = this.add.graphics()
    this.drawManaBar(1)

    // Mana icon
    const manaIcon = this.add.graphics()
    manaIcon.fillStyle(0x3b82f6)
    manaIcon.fillCircle(145, 53, 8)

    // Mana text
    this.manaText = this.add.text(60, 48, '50/50', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Arial'
    }).setOrigin(0.5, 0)

    // XP bar background
    const xpBarBg = this.add.graphics()
    xpBarBg.fillStyle(0x333333, 1)
    xpBarBg.fillRoundedRect(10, 66, 160, 10, 3)

    // XP bar fill
    this.xpBarFill = this.add.graphics()
    this.drawXpBar(0)

    // XP text
    this.xpText = this.add.text(90, 68, '0 XP', {
      fontSize: '9px',
      color: '#a3a3a3',
      fontFamily: 'Arial'
    }).setOrigin(0.5, 0)

    this.healthContainer.add([
      healthBg,
      this.levelText,
      hpBarBg,
      this.healthBarFill,
      healthIcon,
      this.healthText,
      mpBarBg,
      this.manaBarFill,
      manaIcon,
      this.manaText,
      xpBarBg,
      this.xpBarFill,
      this.xpText
    ])
  }

  private drawHealthBar(percent: number): void {
    this.healthBarFill.clear()
    if (percent > 0) {
      const color = percent > 0.5 ? 0xef4444 : percent > 0.25 ? 0xfbbf24 : 0xdc2626
      this.healthBarFill.fillStyle(color, 1)
      this.healthBarFill.fillRoundedRect(10, 28, 120 * percent, 14, 4)
    }
  }

  private drawManaBar(percent: number): void {
    this.manaBarFill.clear()
    if (percent > 0) {
      this.manaBarFill.fillStyle(0x3b82f6, 1)
      this.manaBarFill.fillRoundedRect(10, 46, 120 * percent, 14, 4)
    }
  }

  private drawXpBar(percent: number): void {
    this.xpBarFill.clear()
    if (percent > 0) {
      this.xpBarFill.fillStyle(0x22c55e, 1)
      this.xpBarFill.fillRoundedRect(10, 66, 160 * percent, 10, 3)
    }
  }

  private updateStatBars(): void {
    if (!this.gameScene?.player) return

    const stats = this.gameScene.player.stats

    // Update health
    const hpPercent = stats.currentHp / stats.maxHp
    this.drawHealthBar(hpPercent)
    this.healthText.setText(`${stats.currentHp}/${stats.maxHp}`)

    // Update mana
    const mpPercent = stats.currentMp / stats.maxMp
    this.drawManaBar(mpPercent)
    this.manaText.setText(`${stats.currentMp}/${stats.maxMp}`)

    // Update XP
    const prevLevelXp = stats.level > 1
      ? this.gameScene.player.calculateXPForLevel(stats.level)
      : 0
    const currentXp = stats.experience - prevLevelXp
    const xpNeeded = stats.experienceToNextLevel - prevLevelXp
    const xpPercent = Math.min(1, currentXp / xpNeeded)

    this.drawXpBar(xpPercent)
    this.xpText.setText(`${stats.experience} / ${stats.experienceToNextLevel} XP`)

    // Update level
    this.levelText.setText(`Level ${stats.level}`)
  }

  private createTargetInfo(): void {
    const padding = 15

    // Container for target info (positioned below health)
    this.targetContainer = this.add.container(padding, 110)
    this.targetContainer.setVisible(false)

    // Background
    const targetBg = this.add.graphics()
    targetBg.fillStyle(0x1f2937, 0.9)
    targetBg.fillRoundedRect(0, 0, 150, 45, 8)
    targetBg.lineStyle(2, 0xef4444, 0.5)
    targetBg.strokeRoundedRect(0, 0, 150, 45, 8)

    // Target name
    this.targetNameText = this.add.text(10, 6, '', {
      fontSize: '12px',
      color: '#ef4444',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    })

    // Target health bar background
    const targetHpBg = this.add.graphics()
    targetHpBg.fillStyle(0x333333, 1)
    targetHpBg.fillRoundedRect(10, 24, 100, 12, 3)

    // Target health bar fill
    this.targetHealthBarFill = this.add.graphics()

    // Target health text
    this.targetHealthText = this.add.text(60, 26, '', {
      fontSize: '10px',
      color: '#ffffff',
      fontFamily: 'Arial'
    }).setOrigin(0.5, 0)

    this.targetContainer.add([
      targetBg,
      this.targetNameText,
      targetHpBg,
      this.targetHealthBarFill,
      this.targetHealthText
    ])
  }

  private updateTargetInfo(target: any): void {
    if (!target) {
      this.targetContainer.setVisible(false)
      return
    }

    this.targetContainer.setVisible(true)

    // Update target name
    this.targetNameText.setText(target.definition?.name || 'Unknown')

    // Update target health
    const currentHp = target.getCurrentHp?.() || 0
    const maxHp = target.getMaxHp?.() || 1
    const hpPercent = currentHp / maxHp

    this.targetHealthBarFill.clear()
    if (hpPercent > 0) {
      const color = hpPercent > 0.5 ? 0x22c55e : hpPercent > 0.25 ? 0xfbbf24 : 0xef4444
      this.targetHealthBarFill.fillStyle(color, 1)
      this.targetHealthBarFill.fillRoundedRect(10, 24, 100 * hpPercent, 12, 3)
    }

    this.targetHealthText.setText(`${currentHp}/${maxHp}`)
  }

  private createMinimap(): void {
    const padding = 15
    const size = 100

    // Position in top-right
    this.minimapContainer = this.add.container(0, padding)

    // Background
    const minimapBg = this.add.graphics()
    minimapBg.fillStyle(0x1f2937, 0.9)
    minimapBg.fillRoundedRect(0, 0, size, size, 10)
    minimapBg.lineStyle(2, 0x4b5563)
    minimapBg.strokeRoundedRect(0, 0, size, size, 10)

    // Minimap content
    const minimapContent = this.add.graphics()

    // Player dot
    this.minimapPlayer = this.add.graphics()
    this.minimapPlayer.fillStyle(0xfbbf24)
    this.minimapPlayer.fillCircle(0, 0, 4)

    this.minimapContainer.add([
      minimapBg,
      minimapContent,
      this.minimapPlayer
    ])

    // Draw static minimap
    this.drawMinimap(minimapContent, size)
  }

  private drawMinimap(content: Phaser.GameObjects.Graphics, size: number): void {
    const mapScale = size / (50 * 32) // map is 50 tiles * 32px

    content.clear()

    // Simple representation of terrain
    // Town area
    content.fillStyle(0x9ca3af, 0.8)
    content.fillRect(
      22 * 32 * mapScale + 5,
      22 * 32 * mapScale + 5,
      12 * 32 * mapScale,
      12 * 32 * mapScale
    )

    // Roads
    content.fillStyle(0xd1d5db, 0.6)
    content.fillRect(5, 27 * 32 * mapScale + 5, size - 10, 2 * 32 * mapScale)
    content.fillRect(27 * 32 * mapScale + 5, 5, 2 * 32 * mapScale, size - 10)
  }

  private createPositionIndicator(): void {
    const padding = 15

    // Position text at bottom-left
    this.positionText = this.add.text(padding, 0, 'Pos: 0, 0', {
      fontSize: '14px',
      color: '#9ca3af',
      fontFamily: 'Arial',
      backgroundColor: '#1f293799',
      padding: { x: 8, y: 4 }
    })
  }

  private createControlHints(): void {
    // Show hint for mobile users
    const isMobile = this.sys.game.device.input.touch

    if (isMobile) {
      this.controlHint = this.add.text(0, 0, 'Tap creatures to attack', {
        fontSize: '14px',
        color: '#6b7280',
        fontFamily: 'Arial',
        backgroundColor: '#1f293799',
        padding: { x: 10, y: 6 }
      }).setOrigin(0.5)

      // Fade out after 3 seconds
      this.time.delayedCall(3000, () => {
        if (this.controlHint) {
          this.tweens.add({
            targets: this.controlHint,
            alpha: 0,
            duration: 1000,
            onComplete: () => {
              if (this.controlHint) {
                this.controlHint.destroy()
              }
            }
          })
        }
      })
    }
  }

  private createDeathScreen(): void {
    this.deathScreen = new DeathScreen(this)

    // Set respawn callback
    this.deathScreen.onRespawn = () => {
      if (this.gameScene?.player) {
        this.gameScene.player.respawn()
      }
    }
  }

  private createActionButtons(): void {
    // Get spell system from game scene
    if (this.gameScene?.spellSystem) {
      this.actionButtons = new ActionButtons(
        this,
        this.gameScene.spellSystem,
        this.gameScene.inventorySystem
      )
    }
  }

  private createGoldDisplay(): void {
    const padding = 15

    // Gold display container (positioned below health bar)
    this.goldDisplay = this.add.container(padding, 110)

    // Background
    const bg = this.add.graphics()
    bg.fillStyle(0x1f2937, 0.9)
    bg.fillRoundedRect(0, 0, 100, 32, 8)
    bg.lineStyle(1, 0x4b5563)
    bg.strokeRoundedRect(0, 0, 100, 32, 8)

    // Gold coin icon
    const coinIcon = this.add.graphics()
    coinIcon.fillStyle(0xffd700)
    coinIcon.fillCircle(20, 16, 10)
    coinIcon.fillStyle(0xffed4a)
    coinIcon.fillCircle(18, 14, 5)

    // Gold amount text
    this.goldAmountText = this.add.text(38, 8, '0', {
      fontSize: '16px',
      color: '#fbbf24',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    })

    this.goldDisplay.add([bg, coinIcon, this.goldAmountText])
  }

  private updateGoldDisplay(gold: number): void {
    if (this.goldAmountText) {
      this.goldAmountText.setText(gold.toString())
    }
  }

  private createInventoryButton(): void {
    const padding = 15
    const buttonSize = 50

    // Container for the bag icon button
    this.inventoryButton = this.add.container(padding + buttonSize / 2, 0)
    this.inventoryButton.setDepth(1000)

    // Background circle
    const bg = this.add.circle(0, 0, buttonSize / 2, 0x2d3748)
    bg.setStrokeStyle(2, 0x4a5568)
    bg.setInteractive({ useHandCursor: true })

    // Bag icon
    const bagIcon = this.add.graphics()
    // Bag body
    bagIcon.fillStyle(0x8b4513)
    bagIcon.fillEllipse(0, 2, 22, 16)
    // Bag top
    bagIcon.fillStyle(0xa0522d)
    bagIcon.fillEllipse(0, -6, 14, 8)
    // Bag tie
    bagIcon.fillStyle(0x654321)
    bagIcon.fillRect(-3, -9, 6, 5)
    // Highlight
    bagIcon.fillStyle(0xcd853f, 0.5)
    bagIcon.fillEllipse(-4, 0, 8, 5)

    this.inventoryButton.add([bg, bagIcon])

    // Click to toggle inventory
    bg.on('pointerdown', () => {
      if (this.inventoryUI) {
        this.inventoryUI.toggle()
      }
    })
    bg.on('pointerover', () => bg.setFillStyle(0x4a5568))
    bg.on('pointerout', () => bg.setFillStyle(0x2d3748))
  }

  private createInventoryUI(): void {
    if (this.gameScene?.inventorySystem) {
      this.inventoryUI = new InventoryUI(this, this.gameScene.inventorySystem)
    }
  }

  private showLevelUpNotification(event: LevelUpEvent): void {
    // Remove existing notification if any
    if (this.levelUpContainer) {
      this.levelUpContainer.destroy()
    }

    const centerX = this.scale.width / 2
    const centerY = this.scale.height / 2 - 50

    this.levelUpContainer = this.add.container(centerX, centerY)
    this.levelUpContainer.setDepth(1500)

    // Background
    const bg = this.add.graphics()
    bg.fillStyle(0x1f2937, 0.95)
    bg.fillRoundedRect(-150, -50, 300, 100, 12)
    bg.lineStyle(3, 0xfbbf24)
    bg.strokeRoundedRect(-150, -50, 300, 100, 12)

    // Level up text
    const titleText = this.add.text(0, -30, 'LEVEL UP!', {
      fontSize: '28px',
      color: '#fbbf24',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    // New level
    const levelText = this.add.text(0, 0, `Level ${event.newLevel}`, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial'
    }).setOrigin(0.5)

    // Stats gained
    const statsText = this.add.text(0, 28, `+${event.hpGain} HP  +${event.mpGain} MP  +${event.attackGain} ATK  +${event.defenseGain} DEF`, {
      fontSize: '12px',
      color: '#22c55e',
      fontFamily: 'Arial'
    }).setOrigin(0.5)

    this.levelUpContainer.add([bg, titleText, levelText, statsText])

    // Animate in
    this.levelUpContainer.setScale(0.5)
    this.levelUpContainer.setAlpha(0)

    this.tweens.add({
      targets: this.levelUpContainer,
      scale: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut'
    })

    // Fade out after delay
    this.time.delayedCall(2500, () => {
      if (this.levelUpContainer) {
        this.tweens.add({
          targets: this.levelUpContainer,
          alpha: 0,
          y: centerY - 50,
          duration: 500,
          onComplete: () => {
            if (this.levelUpContainer) {
              this.levelUpContainer.destroy()
              this.levelUpContainer = undefined
            }
          }
        })
      }
    })
  }

  private handleResize(): void {
    const width = this.scale.width
    const height = this.scale.height
    const padding = 15

    // Reposition minimap to top-right
    if (this.minimapContainer) {
      this.minimapContainer.setPosition(width - 100 - padding, padding)
    }

    // Reposition position text to bottom-left
    if (this.positionText) {
      this.positionText.setPosition(padding, height - 35)
    }

    // Center control hint
    if (this.controlHint) {
      this.controlHint.setPosition(width / 2, height - 80)
    }

    // Reposition inventory button to bottom-left, above joystick area
    if (this.inventoryButton) {
      this.inventoryButton.setPosition(padding + 25, height - 180)
    }
  }

  update(): void {
    if (!this.gameScene || !this.gameScene.player) return

    const player = this.gameScene.player

    // Update position text
    const tileX = Math.floor(player.x / 32)
    const tileY = Math.floor(player.y / 32)
    this.positionText.setText(`Pos: ${tileX}, ${tileY}`)

    // Update minimap player position
    const mapScale = 100 / (50 * 32)
    this.minimapPlayer.setPosition(
      player.x * mapScale + 5,
      player.y * mapScale + 5
    )

    // Update target info if visible
    if (this.targetContainer.visible && this.gameScene.combatSystem) {
      const target = this.gameScene.combatSystem.getTarget()
      if (target) {
        this.updateTargetInfo(target)
      }
    }

    // Update action buttons
    if (this.actionButtons) {
      this.actionButtons.update()
    }

    // Update inventory UI if open
    if (this.inventoryUI) {
      this.inventoryUI.update()
    }
  }

  shutdown(): void {
    // Clean up
    if (this.inventoryUI) {
      this.inventoryUI.destroy()
    }
    if (this.actionButtons) {
      this.actionButtons.destroy()
    }
    if (this.deathScreen) {
      this.deathScreen.destroy()
    }
  }
}
